import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	cupService,
	CupRequest,
	CupUpdateRequest,
	CupSearchParams,
} from "../services/cup.service";

// Re-export types for convenience
export type { CupRequest, CupUpdateRequest, CupSearchParams };

export const useCups = () => {
	return useQuery({
		queryKey: ["cups"],
		queryFn: cupService.getCups,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useCupById = (id: string) => {
	return useQuery({
		queryKey: ["cup", id],
		queryFn: () => cupService.getCupById(id),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
	});
};

export const useSearchCups = (params: CupSearchParams) => {
	return useQuery({
		queryKey: ["cups", "search", params],
		queryFn: () => cupService.searchCups(params),
		enabled: Object.keys(params).length > 0,
		staleTime: 2 * 60 * 1000, // 2 minutes
	});
};

export const useCreateCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CupRequest) => cupService.createCup(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
		},
	});
};

export const useUpdateCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CupUpdateRequest) => cupService.updateCup(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
			queryClient.invalidateQueries({ queryKey: ["cup", data._id] });
		},
	});
};

export const useDeleteCup = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => cupService.deleteCup(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["cups"] });
		},
	});
};
